<template>
  <div>
    <h3 class="text text-l-large-bold grey--text text--darken-2">Selected plan</h3>
    <PlanCardOnboardingComponent
      :plan-info="planSelected"
      :show-per-year="true"
      :is-preview-on-checkout="true"
    />
    <div
      v-if="!showDiscountCodeField && !hideAddDiscountActionIfPlanIsLifetime"
      class="mt-6 mb-8"
    >
      <v-row
        no-gutters
        justify="center"
        class="text-center"
      >
        <v-col>
          <p
            class="primary--text text text-l-large-regular-link clickable mb-0 d-flex align-start justify-center"
            @click="showDiscountCodeField = true"
          >
            <ph-plus
              weight="bold"
              size="16"
              class="primary--text mr-2 icon-plus"
            />Add discount code
          </p>
        </v-col>
      </v-row>
    </div>
    <div
      v-if="showDiscountCodeField && !hideAddDiscountActionIfPlanIsLifetime"
      class="mt-4 mb-6"
    >
      <v-row
        no-gutters
        class="card-base-background shadow-card border-radius-small px-4 pt-4 mt-4"
      >
        <v-col cols="12">
          <v-text-field
            v-model="discountPromoCode"
            class="field-custom-label"
            placeholder="Write your discount code"
            height="46"
            dense
            outlined
          >
            <template #prepend>
              <label class="text text-m-small-bold">Discount code</label>
            </template>
          </v-text-field>
        </v-col>
      </v-row>
      <v-row
        no-gutters
        class="mt-4"
      >
        <v-col>
          <v-btn
            ripple
            depressed
            height="44px"
            block
            color="white"
            class="heading heading-s-small primary--text shadow-light-button"
            @click="submitDiscountCode"
          >
            Submit discount code
          </v-btn>
        </v-col>
      </v-row>
    </div>
    <div>
      <h3 class="text text-l-large-bold grey--text text--darken-2 mb-2">Payment details</h3>
      <p
        v-if="!isLifetimePlan"
        class="text grey--text text--darken-2"
        :class="{
          'text-l-small-regular': $vuetify.breakpoint.smAndUp,
          'text-l-large-regular': $vuetify.breakpoint.xsOnly,
        }"
      >
        You will be charged
        {{ planSelected.yearCycle === '1' ? 'yearly' : 'every ' + planSelected.yearCycle + ' years' }}. Cancel your
        subscription or revise your plan any time from your profile
      </p>
      <div class="card-base-background shadow-card border-radius-small pa-4 mb-6">
        <div class="d-flex justify-space-between">
          <span class="text text-l-large-regular grey--text text--darken-3">{{ planSelected.title }} plan</span>
          <span class="text text-l-large-bold grey--text text--darken-1"
            >${{ isLifetimePlan && planSelected.saveMoney ? planSelected.saveMoney : planSelected.price }}</span
          >
        </div>
        <div
          v-if="(isLifetimePlan && planSelected.hasDiscount) || hasDiscount"
          class="d-flex justify-space-between mt-2"
        >
          <span class="text text-l-large-regular grey--text text--darken-3">{{
            isLifetimePlan ? '40% discount' : 'Discount'
          }}</span>
          <span class="text text-l-large-bold success--text">{{
            isLifetimePlan ? '-$' + planSelected.discountByDefault : discountFormat
          }}</span>
        </div>
        <div v-if="currentPlanPriceHash">
          <v-divider class="my-3" />
          <v-row
            no-gutters
            class="text text-l-small-regular plan-prorate-details border-bottom-line py-4 px-4"
          >
            <v-col
              cols="8"
              class="align-self-center"
            >
              <h4 class="">{{ currentPlanName }} plan</h4>
              <p class="mb-0">You have already paid</p>
            </v-col>
            <v-col
              cols="4"
              class="text-right align-self-center"
            >
              <div class="pr-5">$ {{ remainingAmount }}</div>
            </v-col>
          </v-row>
          <v-row
            no-gutters
            class="text text-l-small-regular plan-prorate-details darker border-bottom-line py-4 px-4"
          >
            <v-col
              cols="8"
              class="align-self-center"
            >
              <h4 class="">{{ planSelected.title }} plan</h4>
              <p class="mb-0">The charge for upgrading is</p>
            </v-col>
            <v-col
              cols="4"
              class="text-right align-self-center"
            >
              <div class="pr-5">$ {{ unusedAmount }}</div>
            </v-col>
          </v-row>
          <v-row
            no-gutters
            class="text text-l-small-regular plan-prorate-details darker py-4 px-4"
          >
            <v-col
              cols="8"
              class="align-self-center"
            >
              <h4 class="">Total amount</h4>
            </v-col>
            <v-col
              cols="4"
              class="text-right align-self-center"
            >
              <div class="text text-l-large-bold pr-5 primary--text">$ {{ totalAmount }}</div>
            </v-col>
          </v-row>
        </div>
        <v-divider class="my-3" />
        <div class="d-flex justify-space-between">
          <span class="heading heading-s-small grey--text text--darken-4">Total payment</span>
          <span
            v-if="hasDiscount"
            class="heading heading-s-large primary--text"
            >${{ totalWithDiscount }}</span
          >
          <span
            v-else
            class="heading heading-s-large primary--text"
            >${{
              currentPlanPriceHash
                ? totalAmount
                : new Intl.NumberFormat('en-IN', { maximumSignificantDigits: 3 }).format(
                    isLifetimePlan ? planSelected.priceWithDiscount : planSelected.price,
                  )
            }}</span
          >
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import PlanCardOnboardingComponent from '@/components/subscriptions/PlanCardOnboardingComponent';
import { PhPlus } from 'phosphor-vue';
import { mapActions } from 'vuex';
export default {
  components: { PlanCardOnboardingComponent, PhPlus },
  props: ['planSelected', 'currentPlanPriceHash', 'currentPlanName', 'remainingAmount', 'unusedAmount', 'totalAmount'],
  data: () => ({
    discountPromoCode: null,
    hasDiscount: false,
    discount: 0,
    isPercent: false,
    totalWithDiscount: 0,
    showDiscountCodeField: false,
  }),
  computed: {
    discountFormat() {
      return `-$${this.addDiscountToTotal()}`;
    },
    hideAddDiscountActionIfPlanIsLifetime() {
      return this.isLifetimePlan;
    },
    isLifetimePlan() {
      return this.planSelected.title === 'Lifetime';
    },
  },
  methods: {
    ...mapActions('subscriptions', ['getDiscountByPromoCode']),
    async submitDiscountCode() {
      const response = await this.getDiscountByPromoCode({ params: { promoCode: this.discountPromoCode } });
      let basePrice = this.currentPlanPriceHash ? this.totalAmount : this.planSelected.price;

      if (response?.success) {
        this.hasDiscount = response.success;
        this.discount = parseFloat(response.discount.toString().replace('$', ''));
        this.isPercent = response.isPercent;
        this.totalWithDiscount = (basePrice - this.addDiscountToTotal()).toFixed(2);
        this.$emit('use-promo-code', this.discountPromoCode);
      }

      if (!response?.success) {
        this.hasDiscount = response.success;
        this.discount = response.discount;
        this.isPercent = false;
        this.totalWithDiscount = 0;

        this.$toasted.global.showError({ message: response.message });
        this.$emit('use-promo-code', null);
      }
    },
    addDiscountToTotal() {
      if (this.isPercent) {
        let basePrice = this.currentPlanPriceHash ? this.totalAmount : this.planSelected.price;

        return ((this.discount / 100) * basePrice).toFixed(2);
      }

      return this.discount;
    },
  },
};
</script>
<style scoped>
.border-bottom-line {
  border-bottom: 1px solid #dcd9d9;
}
.icon-plus {
  height: 24px;
}
</style>
